import { Check, Crown, LockKeyhole, WifiOff } from "lucide-react";
import { ROLE_LABELS, type PublicPlayer, type VoiceParticipant } from "@ma-soi/game-core";
import { SeatAvatar } from "./SeatAvatar";
import { VoicePlayerBadge } from "./VoicePlayerBadge";
import { ClawMark } from "./game/PhaseArt";

export function PlayerGrid({
  players,
  selectable = false,
  selectedId,
  onSelect,
  disabledIds = [],
  voiceParticipants = [],
  controlsLocked = false,
  selectionState = "selected",
}: {
  players: PublicPlayer[];
  selectable?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  disabledIds?: string[];
  voiceParticipants?: VoiceParticipant[];
  controlsLocked?: boolean;
  selectionState?: "selected" | "sending" | "confirmed";
}) {
  const voiceByPlayer = new Map(voiceParticipants.map((participant) => [participant.playerId, participant]));
  return (
    <div className={`player-grid ${selectable ? "player-grid-selectable" : ""}`}>
      {players.map((player) => {
        const disabled = disabledIds.includes(player.uid) || !player.alive;
        const selected = selectedId === player.uid;
        const voice = player.connected !== false && player.alive ? voiceByPlayer.get(player.uid) : undefined;
        const Component = selectable ? "button" : "div";
        return (
          <Component
            key={player.uid}
            {...(selectable ? {
              type: "button" as const,
              onClick: () => !disabled && !controlsLocked && onSelect?.(player.uid),
              "aria-pressed": selected,
              "aria-label": `${selected ? "Đã chọn" : "Chọn"} ${player.name}`,
            } : {})}
            className={`player-card ${selected ? `selected selection-${selectionState}` : ""} ${!player.alive ? "dead" : selectable && disabled ? "unavailable" : ""} ${voice?.isSpeaking && player.connected !== false && player.alive ? `voice-speaking voice-${voice.channel}` : ""}`}
            disabled={selectable ? disabled || controlsLocked : undefined}
          >
            {selected && <ClawMark className="selection-claw" />}
            <span className="player-card-portrait">
              <SeatAvatar seat={player.seat} avatarId={player.avatarId} name={player.name} muted={!player.alive} />
              {selected && <span className="player-selected-mark"><Check /></span>}
              {!player.alive && <span className="player-dead-mark"><LockKeyhole /></span>}
              <VoicePlayerBadge participant={voice} speakingOnly />
            </span>
            <span className="player-card-copy">
              <span className="player-name-line">
                <strong>{player.name}</strong>
                {player.isHost && <Crown className="host-crown" aria-label="Chủ phòng" />}
              </span>
              <small>{!player.alive ? "Đã bị loại" : selectable && disabled ? "Không thể chọn" : selected ? selectionState === "sending" ? "Đang gửi" : selectionState === "confirmed" ? "Đã xác nhận" : "Đang chọn" : player.connected === false ? "Mất kết nối" : voice?.isSpeaking ? "Đang nói" : "Còn sống"}</small>
            </span>
            {player.connected === false && <WifiOff className="player-offline" aria-label="Mất kết nối" />}
            <VoicePlayerBadge participant={voice} />
            {player.revealedRole && <span className="role-chip">{ROLE_LABELS[player.revealedRole]}</span>}
          </Component>
        );
      })}
    </div>
  );
}
